import { Line } from 'recharts'

import { baseKey } from './cartesianChart'
import type { ChartDataRow } from './cartesianChart'
import { SummaryMode } from './types'
import type { ChartSeries } from './types'

import { buildLineSegments } from './buildLineSegments'

import type { JSX } from 'react'

// ── Line styling ──────────────────────────────────────────────────────────────
export const DOT_RADIUS = 4
export const LINE_STROKE_WIDTH = 2
/** Hollow (no-data) markers use a smaller radius so their stroked outer edge
 *  (r + strokeWidth/2) matches the filled dots' radius, rather than reading larger. */
export const HOLLOW_DOT_RADIUS = DOT_RADIUS - 1
/** Dotted dash pattern for the interpolated no-data line (round-capped → dots). */
export const GAP_DASHARRAY = '1 6'
/** Above this many interior interpolated points on a series, drop the hollow ring
 *  markers and show just the dotted no-data line — the rings become noise when
 *  the interpolated stretch is long (e.g. a multi-month range). */
export const GAP_MARKER_LIMIT = 14

/** A Latest series that isn't a comparison overlay gets the split treatment: its
 *  observed stretches draw in colour, its null runs draw as the grey no-data line.
 *  Per-day Totals (bars), comparison overlays, and dashed reference lines (e.g. the
 *  benchmark cohort median) keep their single plain line. */
export function isGreyingEligible(series: ChartSeries): boolean {
  return (
    series.summaryMode !== SummaryMode.TOTAL &&
    series.kind !== 'comparison' &&
    !series.dashed
  )
}

/** Axis assignment shared by the row split and the line layers: a right-axis
 *  series is one grouped under the second base metric when dual axes are active. */
interface AxisOptions {
  useDualAxes: boolean
  baseKeys: string[]
}

function isRightAxis(
  series: ChartSeries,
  { useDualAxes, baseKeys }: AxisOptions,
): boolean {
  return useDualAxes && baseKey(series) === baseKeys[1]
}

interface ActiveDotProps {
  cx?: number
  cy?: number
  payload?: ChartDataRow
}

/** Hover marker for the grey no-data line: a hollow ring at the hovered point,
 *  mirroring the colour line's filled active dot. Skips anchor (observed) points —
 *  there the colour line's filled dot fires, so a ring would double up. */
export function makeGapActiveDot(
  seriesId: string,
  fill: string,
  stroke: string,
): (props: ActiveDotProps) => JSX.Element {
  return function GapActiveDot({ cx, cy, payload }): JSX.Element {
    if (payload?.[seriesId] != null || cx == null || cy == null) {
      return <g />
    }
    return (
      <circle
        cx={cx}
        cy={cy}
        r={HOLLOW_DOT_RADIUS}
        fill={fill}
        stroke={stroke}
        strokeWidth={LINE_STROKE_WIDTH}
      />
    )
  }
}

interface AugmentOptions extends AxisOptions {
  /** Left-axis y-domain floor: the leading-gap ramp rises from here. */
  leftFloor: number
  /** Right-axis y-domain floor (dual-axis series ramp from this instead). */
  rightFloor: number
}

/**
 * Splits each eligible (Latest, non-comparison) series into the colour line plus
 * grey `__gap` / `__gapMarker` / `__dot` layers keyed off its id (see
 * {@link buildLineSegments}). Returns a fresh row array — the caller's rows are not
 * mutated. Each axis has its own floor for the leading-gap ramp; a right-axis
 * series ramps from the right domain's baseline, not the left's.
 */
export function augmentRowsWithNoDataSegments(
  rows: readonly ChartDataRow[],
  series: ChartSeries[],
  { leftFloor, rightFloor, useDualAxes, baseKeys }: AugmentOptions,
): ChartDataRow[] {
  const out: ChartDataRow[] = rows.map((row) => ({ ...row }))
  for (const s of series) {
    if (!isGreyingEligible(s)) continue
    const baseValue = isRightAxis(s, { useDualAxes, baseKeys })
      ? rightFloor
      : leftFloor
    const { solid, gap, gapMarkers, dots } = buildLineSegments(
      out.map((row) => {
        const value = row[s.id]
        return {
          timestamp: row.timestamp,
          value: typeof value === 'number' ? value : null,
        }
      }),
      baseValue,
    )
    // Drop the hollow rings once an interpolated stretch is long — a dense run of
    // rings reads as noise; the dotted grey line alone conveys "no data" there.
    const markerCount = gapMarkers.filter((v) => v != null).length
    const showRings = markerCount <= GAP_MARKER_LIMIT
    out.forEach((row, i) => {
      row[s.id] = solid[i]
      row[`${s.id}__gap`] = gap[i]
      row[`${s.id}__gapMarker`] = showRings ? gapMarkers[i] : null
      row[`${s.id}__dot`] = dots[i]
    })
  }
  return out
}

interface GapLineOptions extends AxisOptions {
  noDataColor: string
  surfaceColor: string
  disableAnimations: boolean
}

/**
 * The grey `__gap` line + hollow `__gapMarker` rings for every eligible series.
 * Render these *before* the colour lines so they sit under them (draw order is
 * z-order) — grey never shows over an observed segment. Returns a flat array of
 * `<Line>` elements; recharts flattens array children, so place it as a child.
 */
export function renderNoDataGapLines(
  series: ChartSeries[],
  {
    noDataColor,
    surfaceColor,
    useDualAxes,
    baseKeys,
    disableAnimations,
  }: GapLineOptions,
): JSX.Element[] {
  return series.flatMap((s): JSX.Element[] => {
    if (!isGreyingEligible(s)) return []
    const yAxisId = isRightAxis(s, { useDualAxes, baseKeys }) ? 'right' : 'left'
    return [
      <Line
        key={`${s.id}__gap`}
        type="monotone"
        dataKey={`${s.id}__gap`}
        yAxisId={yAxisId}
        stroke={noDataColor}
        strokeWidth={LINE_STROKE_WIDTH}
        strokeDasharray={GAP_DASHARRAY}
        strokeLinecap="round"
        dot={false}
        // Hollow hover dot at no-data points (skips anchors — see renderer).
        activeDot={makeGapActiveDot(s.id, surfaceColor, noDataColor)}
        connectNulls={false}
        isAnimationActive={!disableAnimations}
        legendType="none"
      />,
      // Hollow ring markers at interior interpolated points (stroke-less line).
      <Line
        key={`${s.id}__gapMarker`}
        type="monotone"
        dataKey={`${s.id}__gapMarker`}
        yAxisId={yAxisId}
        stroke="none"
        dot={{
          r: HOLLOW_DOT_RADIUS,
          fill: surfaceColor,
          stroke: noDataColor,
          strokeWidth: LINE_STROKE_WIDTH,
        }}
        activeDot={false}
        connectNulls={false}
        isAnimationActive={false}
        legendType="none"
      />,
    ]
  })
}

interface DotLineOptions extends AxisOptions {
  resolvedColors: Record<string, string>
}

/**
 * Filled dots at real collection points that border a gap or stand alone (a
 * contiguous daily run gets none). Render these *after* the colour lines so the
 * dots sit on top.
 */
export function renderNoDataDotLines(
  series: ChartSeries[],
  { resolvedColors, useDualAxes, baseKeys }: DotLineOptions,
): JSX.Element[] {
  return series.flatMap((s): JSX.Element[] => {
    if (!isGreyingEligible(s)) return []
    const yAxisId = isRightAxis(s, { useDualAxes, baseKeys }) ? 'right' : 'left'
    return [
      <Line
        key={`${s.id}__dot`}
        type="monotone"
        dataKey={`${s.id}__dot`}
        yAxisId={yAxisId}
        stroke="none"
        dot={{
          r: DOT_RADIUS,
          fill: resolvedColors[s.id],
          strokeWidth: 0,
        }}
        activeDot={false}
        connectNulls={false}
        isAnimationActive={false}
        legendType="none"
      />,
    ]
  })
}
